import React, { useState } from "react";
import { useApiData } from "../hooks/useApiData";
import { unwrapArray } from "../utils/api";
import { WEEKDAYS_LONG, WEEKDAYS_SHORT } from "../utils/dailySales";
import { formatNumber } from "./charts/chartTheme";
import LoadingOverlay from "./ui/LoadingOverlay";
import { ChartSkeleton } from "./ui/Skeleton";
import { EmptyState, ErrorState } from "./ui/StateBlock";

/**
 * "Dan i sat": 7 weekdays x 24 hours. Intensity = average revenue per occurrence of that weekday in the
 * period (so a period with five Fridays and four Mondays stays fair).
 */
export default function WeekdayHourHeatmap({ startDate, endDate, warehouse }) {
  const { data: raw, loading, error } = useApiData(
    `/weekday_hour_sales.php?od=${startDate}&do=${endDate}${warehouse ? `&warehouse=${warehouse}` : ""}`
  );
  const [active, setActive] = useState(null);
  const rows = unwrapArray(raw);

  if (error && rows.length === 0) return <ErrorState />;
  if (loading && !raw) return <ChartSkeleton height={220} />;
  if (rows.length === 0) return <EmptyState title="Nema prometa u izabranom periodu" hint="Probaj drugi period ili magacin." />;

  const grid = Array.from({ length: 7 }, () => Array(24).fill(null));
  let max = 0;
  let bestCell = null;
  rows.forEach((r) => {
    const days = Number(r.dana) || 1;
    const avg = (Number(r.vrednost) || 0) / days;
    grid[r.dan][r.sat] = { avg, total: Number(r.vrednost) || 0, bills: Number(r.racuna) || 0, days };
    if (avg > max) {
      max = avg;
      bestCell = { dan: r.dan, sat: r.sat, avg };
    }
  });
  const hours = Array.from({ length: 24 }, (_, h) => h);
  const detail = active ? grid[active.d][active.h] : null;

  return (
    <LoadingOverlay loading={loading}>
      <div className="flex flex-col gap-3">
        {bestCell && (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Najjači termin:{" "}
            <span className="font-medium text-slate-800 dark:text-slate-100">
              {WEEKDAYS_LONG[bestCell.dan].toLowerCase()} u {String(bestCell.sat).padStart(2, "0")}h
            </span>{" "}
            (u proseku {formatNumber(bestCell.avg)} RSD po {WEEKDAYS_LONG[bestCell.dan].toLowerCase()}u). Jače obojeno polje je veći promet.
          </p>
        )}
        <div className="overflow-x-auto">
          <div className="grid min-w-[640px] gap-[3px]" style={{ gridTemplateColumns: "36px repeat(24, minmax(0, 1fr))" }}>
            <div />
            {hours.map((h) => (
              <div key={h} className="text-center text-[10px] tabular-nums text-slate-500 dark:text-slate-400">
                {h}
              </div>
            ))}
            {grid.map((row, d) => (
              <React.Fragment key={d}>
                <div className="flex items-center text-xs text-slate-600 dark:text-slate-300">{WEEKDAYS_SHORT[d]}</div>
                {row.map((cell, h) => {
                  const ratio = cell && max > 0 ? cell.avg / max : 0;
                  const isActive = active && active.d === d && active.h === h;
                  return (
                    <button
                      key={h}
                      type="button"
                      aria-label={`${WEEKDAYS_LONG[d]} ${h}h: ${cell ? formatNumber(cell.avg) : "0"} RSD`}
                      onMouseEnter={() => setActive({ d, h })}
                      onFocus={() => setActive({ d, h })}
                      onClick={() => setActive({ d, h })}
                      className={`relative h-7 rounded-[3px] border border-slate-200 bg-slate-100 dark:border-slate-600/60 dark:bg-slate-700/40 ${isActive ? "ring-2 ring-slate-900 dark:ring-slate-50" : ""}`}
                    >
                      {cell && <span className="absolute inset-0 rounded-[3px] bg-primary dark:bg-primary-light" style={{ opacity: 0.3 + ratio * 0.7 }} />}
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
        <p className="min-h-[2.5rem] rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-600 dark:border-slate-800 dark:text-slate-300">
          {active
            ? detail
              ? `${WEEKDAYS_LONG[active.d]}, ${String(active.h).padStart(2, "0")}h: u proseku ${formatNumber(detail.avg)} RSD po danu, ukupno ${formatNumber(detail.total)} RSD i ${formatNumber(detail.bills)} računa u ${detail.days} ${detail.days === 1 ? "danu" : "dana"} (${WEEKDAYS_LONG[active.d].toLowerCase()}).`
              : `${WEEKDAYS_LONG[active.d]}, ${String(active.h).padStart(2, "0")}h: nema prometa.`
            : "Pređi mišem preko polja (ili ga dodirni) da vidiš iznos."}
        </p>
      </div>
    </LoadingOverlay>
  );
}
